import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, RotateCcw } from 'lucide-react';
import { updateNodeItemOrder } from '../../api';
import useNodeCatalog, { invalidateNodeCatalog } from '../../hooks/useNodeCatalog';
import { useToast } from '../../hooks/use-toast';
import { trackEvent } from '../../lib/usageEvents';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  checklistOrderCharts,
  initialItemOrders,
  moveKey,
  sameOrder,
  toStoredOrder
} from './checklistItemOrder';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

const SCREEN_READER_INSTRUCTIONS = {
  draggable:
    'Чтобы взять пункт, нажмите пробел или Enter. Стрелками вверх и вниз переместите его, '
    + 'пробелом или Enter положите на новое место, Escape — отмена.'
};

/** Объявления для скринридера — по-русски, с подписью пункта и позицией, а не с ключом. */
const buildAnnouncements = (labelOf, positionOf) => ({
  onDragStart: ({ active }) => `Пункт «${labelOf(active.id)}» взят, позиция ${positionOf(active.id)}.`,
  onDragOver: ({ active, over }) =>
    over
      ? `Пункт «${labelOf(active.id)}» над позицией ${positionOf(over.id)}.`
      : `Пункт «${labelOf(active.id)}» вне списка.`,
  onDragEnd: ({ active, over }) =>
    over
      ? `Пункт «${labelOf(active.id)}» перемещён на позицию ${positionOf(over.id)}.`
      : `Пункт «${labelOf(active.id)}» возвращён на место.`,
  onDragCancel: ({ active }) => `Перемещение отменено, пункт «${labelOf(active.id)}» остался на месте.`
});

const SortableItemRow = ({ itemKey, label, position, disabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: itemKey, disabled });

  return (
    <li
      ref={setNodeRef}
      data-item-key={itemKey}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-md border bg-background px-2 py-1.5',
        isDragging && 'relative z-10 shadow-md'
      )}
    >
      {/* Тянется только ручка: так строку можно выделить мышью, а тач-прокрутка страницы
          не превращается в перетаскивание. */}
      <button
        type="button"
        data-ev="settings.checklist_order.drag_handle"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Переместить пункт ${label}`}
        disabled={disabled}
        className="shrink-0 cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{position}.</span>
      <span className="min-w-0 flex-1 text-sm [overflow-wrap:anywhere]">{label}</span>
      <span className="hidden max-w-[40%] break-all font-mono text-xs text-muted-foreground sm:inline">
        {itemKey}
      </span>
    </li>
  );
};

const ChecklistOrderList = ({ chart, keys, pending, onMove, onReset }) => {
  const sensors = useSensors(
    // Порог в несколько пикселей: клик по ручке не должен начинать перетаскивание.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const labelOf = (itemKey) => chart.labels[itemKey] ?? itemKey;
  const positionOf = (itemKey) => keys.indexOf(itemKey) + 1;
  const isCustom = !sameOrder(keys, chart.defaultOrder);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    trackEvent('settings.checklist_order.reorder');
    onMove(keys.indexOf(active.id), keys.indexOf(over.id));
  };

  return (
    <div className="space-y-2 rounded-md border p-3" data-chart-id={chart.id}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium">{chart.title}</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-all font-mono text-xs text-muted-foreground">{chart.id}</span>
            {chart.hidden && (
              <Badge variant="secondary" className="font-normal">
                скрыт на дашборде
              </Badge>
            )}
            {isCustom && (
              <Badge variant="outline" className="font-normal">
                свой порядок
              </Badge>
            )}
          </div>
        </div>
        {isCustom && (
          <Button type="button" data-ev="settings.checklist_order.reset" variant="outline" size="sm" disabled={pending} onClick={onReset}>
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            Сбросить к порядку пакета
          </Button>
        )}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        accessibility={{
          screenReaderInstructions: SCREEN_READER_INSTRUCTIONS,
          announcements: buildAnnouncements(labelOf, positionOf)
        }}
      >
        <SortableContext items={keys} strategy={verticalListSortingStrategy}>
          <ol className="space-y-1" aria-label={`Пункты чек-листа «${chart.title}»`}>
            {keys.map((itemKey, index) => (
              <SortableItemRow
                key={itemKey}
                itemKey={itemKey}
                label={labelOf(itemKey)}
                position={index + 1}
                disabled={pending}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
    </div>
  );
};

/**
 * Порядок пунктов чек-листа на экране: тепловая карта и «По пунктам» на дашборде, фильтр
 * «Провалы по чек-листам» и карточка звонка. По умолчанию — порядок пакета; здесь его можно
 * поменять перетаскиванием (мышью, пальцем или с клавиатуры).
 *
 * Как и переименования, это настройка показа: пакет, промпты и результаты анализа не меняются,
 * выбор переживает перезаливку пакета, а новые пункты следующей версии встают в конец.
 */
const ChecklistItemOrderCard = () => {
  const { toast } = useToast();
  const { catalog, isLoading, isActive } = useNodeCatalog();

  // Оптимистичное состояние, как у галок видимости: каталог после записи не перечитываем,
  // иначе список мигнул бы и мог затереть ещё не сохранённый соседний перенос.
  const [orders, setOrders] = useState(null);
  const [pending, setPending] = useState({});

  useEffect(() => {
    if (!isLoading && catalog) {
      setOrders(initialItemOrders(catalog));
    }
  }, [isLoading, catalog]);

  const charts = useMemo(() => checklistOrderCharts(catalog), [catalog]);

  const saveOrder = async (chart, previous, next) => {
    setOrders((current) => ({ ...current, [chart.id]: next }));
    setPending((current) => ({ ...current, [chart.id]: true }));

    try {
      await updateNodeItemOrder(chart.id, toStoredOrder(next, chart.defaultOrder));
      // Без сброса кэша «По пунктам» и панель фильтров держали бы прежний порядок до
      // перезагрузки вкладки: они берут его из каталога.
      invalidateNodeCatalog();
    } catch (error) {
      setOrders((current) => ({ ...current, [chart.id]: previous }));
      toast({
        title: 'Не удалось сохранить порядок пунктов',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setPending((current) => ({ ...current, [chart.id]: false }));
    }
  };

  if (!isActive || orders === null || charts.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Порядок пунктов чек-листов</CardTitle>
        <CardDescription>
          В каком порядке показывать пункты чек-листа на тепловой карте, в разбивке «По пунктам»,
          в фильтре «Провалы по чек-листам» и в карточке звонка. По умолчанию — как в пакете.
          Перетащите пункт за ручку слева или возьмите её с клавиатуры пробелом и двигайте
          стрелками. Это касается только отображения: пакет и результаты анализа не меняются.
          Порядок сохраняется сразу и переживает перезаливку пакета; новые пункты встают в конец.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {charts.map((chart) => {
          const keys = orders[chart.id] ?? chart.defaultOrder;

          return (
            <ChecklistOrderList
              key={chart.id}
              chart={chart}
              keys={keys}
              pending={pending[chart.id] === true}
              onMove={(from, to) => saveOrder(chart, keys, moveKey(keys, from, to))}
              onReset={() => saveOrder(chart, keys, chart.defaultOrder)}
            />
          );
        })}
      </CardContent>
    </Card>
  );
};

export default ChecklistItemOrderCard;
